import React, { useState } from 'react';
import Breadcrumb from "app/components/Breadcrumb";
import { Delete, Edit, Search, PictureAsPdf, GetApp, Print, ArrowDropDown as ArrowDropDownIcon, Replay } from "@mui/icons-material";
import useZoning from "./hooks/useZoning";
import ZoningEditorDialog from "./components/ZoningEditorDialog";
import ConfirmationDialog from "app/components/ConfirmationDialog";
import { themeShadows } from "app/components/MatxTheme/options/themeColors";
import {
    Box,
    Card,
    styled,
    TextField,
    InputAdornment,
    Button,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import useRoles from "app/hooks/useRoles";
import useExport from 'app/hooks/useExport';

const exportColumns = [
    { label: 'Code', field: 'code' },
    { label: 'Description', field: 'description' },
    { label: 'Status', field: 'status' },
];

const Status = styled("small")(({ theme, color }) => ({
    color: "#fff",
    padding: "2px 8px",
    overflow: "hidden",
    borderRadius: "300px",
    boxShadow: themeShadows[3],
    background: theme.palette[color]?.main || theme.palette.primary.main
}));

export default function Zoning() {
    const [newZoning, setNewZoning] = useState({
        fieldid: '',
        description: '',
        type: ''
    });

    const getrolebyusername = useRoles();
    const isActionEnabled = getrolebyusername.includes('SA') || getrolebyusername.includes('Admin');

    const Container = styled("div")(({ theme }) => ({
        margin: "30px",
        [theme.breakpoints.down("sm")]: { margin: "16px" },
        "& .breadcrumb": {
            marginBottom: "0px",
            [theme.breakpoints.down("sm")]: { marginBottom: "16px" }
        }
    }));

    const handleAddNewUser = () => {
        setZoning(null);
        setShouldOpenEditorDialog(true);
    };

    const {
        handleEditUser,
        handleDeleteUser,
        shouldOpenEditorDialog,
        handleDialogClose,
        zoning,
        setShouldOpenEditorDialog,
        zonings,
        setZoning,
        shouldOpenConfirmationDialog,
        handleConfirmationResponse,
        handleReactivateZoning,
        handleReactivateConfirmationResponse,
        shouldOpenReactivateConfirmationDialog
    } = useZoning();

    const zoningsForExport = zonings.map(z => ({
        ...z,
        status: z.statusLUId === 1 ? 'Active' : z.statusLUId === 2 ? 'Default' : 'Deleted'
    }));
    const { exportToPDF, exportToExcel, exportToCSV, handlePrint } = useExport(zoningsForExport, exportColumns, "Zoning", "table");

    const [anchorEl, setAnchorEl] = useState(null);
    const handleExportMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleExportMenuClose = () => setAnchorEl(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [showDeleted, setShowDeleted] = useState(false);

    const STATUSTYPES = {
        ACTIVE: 1,
        DEFAULT: 2,
        DELETED: 3,
    };

    const gridColumns = [
        { field: 'code', headerName: 'Code', flex: 1 },
        { field: 'description', headerName: 'Description', flex: 2 },
        {
            field: 'statusLUId', headerName: 'Status', flex: 1,
            renderCell: (params) => {
                const val = params.value;
                if (val === STATUSTYPES.ACTIVE) return <Status color="success">Active</Status>;
                if (val === STATUSTYPES.DEFAULT) return <Status color="primary">Default</Status>;
                return <Status color="error">Deleted</Status>;
            }
        },
        ...(isActionEnabled ? [{
            field: 'actions', headerName: 'Action', flex: 1, sortable: false,
            renderCell: (params) => {
                const z = params.row;
                return (
                    <>
                        <Tooltip title="Edit">
                            <span>
                                <IconButton color="primary" onClick={(e) => { e.stopPropagation(); handleEditUser(z)(e); }} disabled={z.statusLUId === STATUSTYPES.DEFAULT || z.statusLUId === STATUSTYPES.DELETED}>
                                    <Edit />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {z.statusLUId === STATUSTYPES.DELETED ? (
                            <Tooltip title="Reactivate">
                                <IconButton color="success" onClick={() => handleReactivateZoning(z)}>
                                    <Replay />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Delete">
                                <span>
                                    <IconButton color="error" onClick={() => handleDeleteUser(z)} disabled={z.statusLUId === STATUSTYPES.DEFAULT}>
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </>
                );
            }
        }] : [])
    ];

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleToggleShowDeleted = () => {
        setShowDeleted(prev => !prev);
    };

    const filteredZoningList = zonings.filter(zoningrecord =>
        (showDeleted || zoningrecord.statusLUId !== STATUSTYPES.DELETED) &&
        (zoningrecord.code.toLowerCase().startsWith(String(searchQuery).toLowerCase()) ||
            zoningrecord.description.toLowerCase().startsWith(String(searchQuery).toLowerCase()))
    );

    const handleInputChange = (e) => {
        setNewZoning({ ...newZoning, [e.target.name]: e.target.value });
    };

    return (
        <Container>
            <div className="breadcrumb">
                <Breadcrumb routeSegments={[{ name: "Zoning" }]} />
            </div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    sx={{ mb: 2, mt: 2 }}
                    color="primary"
                    variant="contained"
                    onClick={handleAddNewUser}
                >
                    Add Zoning
                </Button>

                <TextField
                    label="Search by Code or Description"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ width: '300px' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        sx: { height: '40px' }
                    }}
                />
            </Box>
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={handlePrint}
                        startIcon={<Print />}
                    >
                        Print
                    </Button>
                    <Button
                        color="success"
                        variant="contained"
                        onClick={handleExportMenuOpen}
                        startIcon={<GetApp />}
                        endIcon={<ArrowDropDownIcon />}
                        sx={{ ml: 2 }}
                    >
                        Export
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleExportMenuClose}
                    >
                        <MenuItem onClick={() => { exportToPDF(); handleExportMenuClose(); }}>
                            <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                            <ListItemText primary="PDF" />
                        </MenuItem>
                        <MenuItem onClick={() => { exportToExcel(); handleExportMenuClose(); }}>
                            <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Excel" />
                        </MenuItem>
                        <MenuItem onClick={() => { exportToCSV(); handleExportMenuClose(); }}>
                            <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                            <ListItemText primary="CSV" />
                        </MenuItem>
                    </Menu>
                </Box>
                <FormControlLabel
                    control={<Switch checked={showDeleted} onChange={handleToggleShowDeleted} />}
                    label="Show Deleted"
                />
            </Box>

            <Card sx={{ width: "100%", overflow: "auto" }} elevation={6}>
                <DataGrid
                    rows={filteredZoningList}
                    columns={gridColumns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    autoHeight
                    disableSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f5f5f5',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        },
                    }}
                />
            </Card>

            {
                shouldOpenEditorDialog && (
                    <ZoningEditorDialog
                        open={shouldOpenEditorDialog}
                        handleClose={handleDialogClose}
                        zoning={zoning}
                    />
                )
            }

            {
                shouldOpenConfirmationDialog && (
                    <ConfirmationDialog
                        open={shouldOpenConfirmationDialog}
                        onConfirmDialogClose={handleDialogClose}
                        onYesClick={handleConfirmationResponse}
                        title="Are you sure?"
                        text="Are you sure you want to delete this Zone?"
                    />
                )
            }
            {
                shouldOpenReactivateConfirmationDialog && (
                    <ConfirmationDialog
                        open={shouldOpenReactivateConfirmationDialog}
                        onConfirmDialogClose={handleDialogClose}
                        onYesClick={handleReactivateConfirmationResponse}
                        title="Are you sure?"
                        text="Are you sure you want to reactivate this Zone?"
                    />
                )
            }
        </Container >

    );


}